import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  TextField,
  Box
} from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useEnvironment } from '../../../contexts/EnvironmentContext';

const DeleteConfirmDialog = ({ open, onClose, environment }) => {
  const { selectedOrganization } = useOrganization();
  const { deleteEnvironment, loading } = useEnvironment();
  const [error, setError] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  const handleClose = () => {
    setError(null);
    setConfirmationText('');
    onClose();
  };

  const handleConfirm = async () => {
    if (confirmationText !== environment.name) {
      setError('Environment name does not match');
      return;
    }

    setError(null);

    try {
      await deleteEnvironment(selectedOrganization.id, environment.id);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to delete environment');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && confirmationText === environment.name) {
      handleConfirm();
    }
  };

  if (!environment) return null;

  const isConfirmationValid = confirmationText === environment.name;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Delete Environment
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Warning:</strong> This action cannot be undone. All data associated with this environment will be permanently deleted.
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to permanently delete the environment <strong>"{environment.name}"</strong>?
        </DialogContentText>

        <DialogContentText sx={{ mb: 2 }}>
          To confirm, please type the environment name exactly as shown above:
        </DialogContentText>

        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder={environment.name}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          onKeyPress={handleKeyPress}
          error={!!error && error === 'Environment name does not match'}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading || !isConfirmationValid}
        >
          {loading ? 'Deleting...' : 'Delete Permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
